import React, { useEffect, useState } from "react";
import { Crosshair, ArrowsClockwise, Scales } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Sprint 90 — Leverage Score board. Optimal% (our correlated sims) minus the
// field's REAL ownership, recounted off the lineup sets the user imported.
const fmt = (v, d = 1) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(d));
const signed = (v) => (v == null ? "—" : `${Number(v) > 0 ? "+" : ""}${Number(v).toFixed(1)}`);
const levColor = (v) => {
  const n = Number(v || 0);
  if (n >= 10) return "text-[#39FF14]";
  if (n >= 4) return "text-[#9EFF6A]";
  if (n <= -10) return "text-[#FF3B30]";
  if (n <= -4) return "text-[#FF8A00]";
  return "text-zinc-400";
};
const KIND_LABEL = { qb_pair: "QB stack", bring_back: "Bring-back", team_stack: "Team", game_stack: "Game" };

export default function LeverageBoard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("players");
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    return http.get("/field/leverage?limit=60")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener("wp:field-model-changed", on);
    window.addEventListener("wp:lineups-changed", on);
    return () => {
      window.removeEventListener("wp:field-model-changed", on);
      window.removeEventListener("wp:lineups-changed", on);
    };
  }, []);
  const rc = data?.recount;
  const players = data?.players || [];
  const stacks = data?.stacks || [];
  return (
    <section className="bg-[#121212] border border-[#39FF14]/40 p-4" data-testid="leverage-board">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="heading text-lg font-black uppercase inline-flex items-center gap-2">
            <Scales size={16} weight="fill" className="text-[#39FF14]" /> Leverage Score
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 max-w-2xl leading-relaxed">
            <span className="text-white">Optimal%</span> (how often a player lands in the optimal lineup across our
            correlated slate sims) minus the field's <span className="text-white">real ownership</span>, scaled for
            duplication risk. <span className="text-[#39FF14]">+10 or better</span> is elite leverage,{" "}
            <span className="text-[#FF3B30]">−10 or worse</span> is a chalk trap.
          </div>
        </div>
        <div className="text-right">
          <div className="mono text-[10px] uppercase tracking-widest" data-testid="leverage-own-mode">
            {rc?.n_lineups
              ? <span className={rc.mode === "override" ? "text-[#39FF14] font-bold" : "text-[#FFCC00] font-bold"}>
                  {rc.mode === "override" ? "RECOUNTED" : "BLENDED"} · {rc.n_lineups} field lineups
                </span>
              : <span className="text-zinc-500">projected ownership · import field lineups for exact counts</span>}
          </div>
          {rc?.n_lineups ? (
            <div className="mono text-[9px] text-zinc-500 mt-1">
              {(rc.sources || []).join(" + ")} · {rc.n_players} players counted
              {rc.mode !== "override" ? ` · under ${rc.min_for_override} lineups, blended 50/50 with projections` : ""}
            </div>
          ) : null}
          <button type="button" onClick={load} disabled={busy} data-testid="leverage-refresh"
            className="mt-1 mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#39FF14] inline-flex items-center gap-1">
            <ArrowsClockwise size={11} className={busy ? "animate-spin" : ""} /> {busy ? "working…" : "refresh"}
          </button>
        </div>
      </div>
      <div className="flex gap-1 mb-2">
        {[["players", `Players${players.length ? ` · ${players.length}` : ""}`],
          ["stacks", `Stacks${stacks.length ? ` · ${stacks.length}` : ""}`]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`leverage-tab-${k}`}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${tab === k
              ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
              : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      {!data?.ready ? (
        <div className="mono text-[11px] text-zinc-500 py-6 text-center" data-testid="leverage-empty">
          <Crosshair size={16} className="inline mb-1 text-zinc-700" />
          <div>Load a slate and apply a player pool — the Leverage Score runs off the live sims.</div>
        </div>
      ) : tab === "players" ? (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-zinc-900">
          <table className="w-full mono text-[11px]" data-testid="leverage-players-table">
            <thead className="bg-black sticky top-0">
              <tr className="text-left text-zinc-500 uppercase tracking-widest text-[9px]">
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Pos</th>
                <th className="px-3 py-2" title="How often this player is in the optimal lineup across our sims">Optimal %</th>
                <th className="px-3 py-2" title="The field's ownership we are measuring against">Field %</th>
                <th className="px-3 py-2" title="What the sites projected before we recounted their lineups">Proj %</th>
                <th className="px-3 py-2">Lev Score</th>
                <th className="px-3 py-2">Read</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.dk_id} className="border-t border-zinc-900 hover:bg-white/5" data-testid={`leverage-row-${p.dk_id}`}>
                  <td className="px-3 py-2 text-white">{p.name} <span className="text-zinc-600">{p.team}</span></td>
                  <td className="px-3 py-2 text-zinc-400">{p.position}</td>
                  <td className="px-3 py-2 text-zinc-300">{fmt(p.optimal_pct)}%</td>
                  <td className="px-3 py-2 text-zinc-300"
                      title={p.field_own_mode === "model" ? "No sources loaded — our internal field-ownership model" : undefined}>
                    {fmt(p.field_own_effective ?? p.field_own)}%
                  </td>
                  <td className="px-3 py-2 text-zinc-600">{fmt(p.field_own_csv)}%</td>
                  <td className={`px-3 py-2 font-bold ${levColor(p.leverage_delta)}`}>{signed(p.leverage_delta)}</td>
                  <td className={`px-3 py-2 ${levColor(p.leverage_delta)}`}>{p.leverage_tier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-zinc-900">
          {!stacks.length ? (
            <div className="mono text-[11px] text-zinc-500 py-6 text-center" data-testid="leverage-stacks-empty">
              Import a site's field lineups to recount their stack ownership.
            </div>
          ) : (
            <table className="w-full mono text-[11px]" data-testid="leverage-stacks-table">
              <thead className="bg-black sticky top-0">
                <tr className="text-left text-zinc-500 uppercase tracking-widest text-[9px]">
                  <th className="px-3 py-2">Stack</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2" title="Share of the imported field lineups that carry this combo">Field %</th>
                  <th className="px-3 py-2" title="Share of YOUR current portfolio that carries it">Ours %</th>
                  <th className="px-3 py-2">Lev Score</th>
                </tr>
              </thead>
              <tbody>
                {stacks.map((s) => (
                  <tr key={`${s.kind}-${s.label}`} className="border-t border-zinc-900 hover:bg-white/5"
                      data-testid={`leverage-stack-${s.label.replace(/[^A-Za-z0-9]+/g, "-")}`}>
                    <td className="px-3 py-2 text-white">{s.label}</td>
                    <td className="px-3 py-2 text-zinc-500">{KIND_LABEL[s.kind] || s.kind}</td>
                    <td className="px-3 py-2 text-zinc-300">{fmt(s.field_own)}%</td>
                    <td className="px-3 py-2 text-zinc-300">{fmt(s.our_exposure)}%</td>
                    <td className={`px-3 py-2 font-bold ${levColor(s.leverage)}`}>{signed(s.leverage)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data?.our_lineups === 0 && stacks.length ? (
            <div className="mono text-[9px] text-zinc-600 px-3 py-2 border-t border-zinc-900">
              Build lineups to see your own stack exposure against these numbers.
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
